import { Outlet, useLocation, Link } from "react-router-dom";
import { useState, useEffect, Suspense } from "react";
import { useAppSelector, useAppDispatch } from "@/redux/store";
import {
  selectUser,
  selectIsAuthenticated,
  selectPaymentRequired,
  setUser,
  setPaymentRequired,
} from "@/redux/slices/authSlice";
import { selectTheme, setTheme } from "@/redux/slices/uiSlice";
import InlineSearch from "@/components/view/dashboard/@root/InlineSearch";
import { initSocketService, disconnectSocketService } from "@/lib/utils/socket";
import Loader from "@/components/ui/loader/Loader";
import ActiveCallDock from "@/components/view/dashboard/@root/ActiveCallDock";
import LiveDeck from "@/components/view/dashboard/@root/LiveDeck";
import NotificationBell from "@/components/ui/notifications/NotificationBell";
import { API_URL, authAPI, userSettingsAPI } from "@/lib/services/api.service";
import { useAuthActions } from "@/lib/hooks/useAuthActions";
import Dropdown, { DropdownItem } from "@/components/ui/dropdown/Dropdown";
import Sidebar from "./Sidebar";
import type { User } from "@/redux/slices/authSlice";
import Logo from "@/components/shared/logo/Logo";
import PaymentSetupBanner from "@/components/view/dashboard/@root/PaymentSetupBanner";
import Tooltip from "@/components/ui/tooltip/Tooltip";

function SunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}
function MoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
      />
    </svg>
  );
}
function MonitorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

export default function DashboardLayout() {
  // ============================| Hooks |===========================
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser) as User;
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const paymentRequired = useAppSelector(selectPaymentRequired);
  const billingBlocksLive = paymentRequired && user?.role !== "SUPER_ADMIN";

  // ============================| States |===========================
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [displayOpen, setDisplayOpen] = useState(false);
  const { logout } = useAuthActions();
  const theme = useAppSelector(selectTheme);

  // ============================| Effects |===========================
  // Refresh user data on mount to get signed URLs for profile picture and company logo
  // The login endpoint returns raw paths, but getMe returns signed URLs which are required for images
  useEffect(() => {
    if (user && isAuthenticated) {
      authAPI
        .getMe()
        .then((response: any) => {
          if (response?.user || response?.company) {
            // Merge the updated user data (with signed URLs) into existing user state
            dispatch(
              setUser({
                ...user,
                ...(response.user && {
                  profile_picture: response.user.profile_picture,
                  avatar: response.user.profile_picture,
                }),
                ...(response.company && {
                  company: {
                    ...user.company,
                    ...response.company,
                  },
                }),
              })
            );
            if (typeof response.paymentRequired === "boolean") {
              dispatch(setPaymentRequired(response.paymentRequired));
            }
          }
        })
        .catch((error: any) => {
          console.error("Failed to refresh user data:", error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isAuthenticated, dispatch]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (user && isAuthenticated) {
      const token =
        localStorage.getItem("sb-access-token") || sessionStorage.getItem("sb-access-token") || "";

      const socketService = initSocketService(dispatch, API_URL);
      socketService.connect(user.id, token);

      // Update connection status
      const checkConnection = setInterval(() => {
        setSocketConnected(socketService.isConnected());
      }, 1000);

      return () => {
        clearInterval(checkConnection);
        disconnectSocketService();
      };
    }
  }, [user, isAuthenticated, dispatch]);

  // Check for mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location, isMobile]);

  // Close Display submenu when profile dropdown closes
  useEffect(() => {
    if (!profileOpen) setDisplayOpen(false);
  }, [profileOpen]);

  const handleLogout = async () => {
    disconnectSocketService();
    await logout();
  };

  const avatarUrl = user?.profile_picture ?? user?.avatar ?? null;
  const isWidgetPage = location.pathname === "/widget" || location.pathname.endsWith("/widget");

  const displayOptions = [
    { value: "light" as const, label: "Light", icon: SunIcon },
    { value: "dark" as const, label: "Dark", icon: MoonIcon },
    { value: "auto" as const, label: "Automatic", icon: MonitorIcon },
  ];

  const handleThemeChange = async (nextTheme: "light" | "dark" | "auto") => {
    dispatch(setTheme(nextTheme));

    if (!user?.id) return;

    try {
      await userSettingsAPI.updateUserPreferences({ theme: nextTheme });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("[DashboardLayout] Failed to persist theme preference:", error);
      }
    }
  };

  return (
    <div className="bg-light px-2">
      <header className="h-(--navbar-height) flex items-center justify-between gap-4 pl-4.5 pr-4">
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex items-center shrink-0">
            {user?.company?.logoUrl || user?.company?.logo ? (
              <div className="relative w-20 h-14 flex-center shrink-0">
                <style>{`
                  @keyframes header-logo-in {
                    0%, 40% { opacity: 1; }
                    50%, 90% { opacity: 0; }
                    100% { opacity: 1; }
                  }
                  @keyframes header-company-in {
                    0%, 40% { opacity: 0; }
                    50%, 90% { opacity: 1; }
                    100% { opacity: 0; }
                  }
                `}</style>
                <div className="absolute inset-0 flex-center animate-[header-logo-in_10s_ease-in-out_infinite]">
                  <Logo />
                </div>
                <div className="absolute inset-0 flex-center animate-[header-company-in_10s_ease-in-out_infinite]">
                  <div className="w-20 h-14 rounded-lg overflow-hidden flex-center shrink-0 bg-transparent">
                    <img
                      src={user.company.logoUrl || user.company.logo || ""}
                      alt={user.company.name ? `${user.company.name} logo` : "Company logo"}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <Logo />
            )}
          </div>

          <div className="flex-1 w-full max-w-lg">
            <InlineSearch />
          </div>

          <div className="flex justify-end items-center gap-2 shrink-0">
            {/* Connection Status Indicator */}
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-bg">
              <div
                className={`w-2 h-2 rounded-full ${
                  socketConnected ? "bg-good animate-pulse" : "bg-bad"
                }`}
              />
              <span className="text-xs leading-0.5 text-gray-500">
                {socketConnected ? "Live" : "Offline"}
              </span>
            </div>

            <LiveDeck billingBlocksLive={billingBlocksLive} />

            <NotificationBell />

            {/* Profile – circular avatar with dropdown (using Dropdown component) */}
            <Dropdown
              position="bottom-left"
              triggerClassName="rounded-full"
              open={profileOpen}
              onOpenChange={setProfileOpen}
              gap={0}
              dropDownClass="!p-0 !bg-card !border border-border! !rounded-xl w-56 shadow-xl overflow-hidden"
              trigger={
                <Tooltip content={profileOpen ? null : "Profile"}>
                  <button
                    type="button"
                    className="size-11 rounded-full overflow-hidden bg-light-1 hover:bg-light-2 flex-center shrink-0 transition-colors"
                    aria-label="Profile menu"
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={user?.name ?? "Profile"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-500 text-sm font-medium">
                        {(user?.name ?? "U").charAt(0).toUpperCase()}
                      </span>
                    )}
                  </button>
                </Tooltip>
              }
            >
              <div className="py-3.5 px-4 border-b border-border">
                <p className="text-sm font-medium text-text truncate">{user?.name ?? "User"}</p>
                <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
              </div>
              <nav className="py-2 px-2">
                <Link to="/settings" onClick={() => setProfileOpen(false)} className="block">
                  <DropdownItem
                    className="px-3 py-2.5 hover:bg-light-1/80 text-sm rounded-lg transition-colors text-text"
                    icon={
                      <svg
                        className="w-4 h-4 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    }
                  >
                    Settings
                  </DropdownItem>
                </Link>

                {/* Display: expandable section (stays inside menu, no overflow) */}
                <div className="w-full">
                  <button
                    type="button"
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-light-1/80 text-sm font-medium text-text text-left transition-colors rounded-lg"
                    onClick={() => setDisplayOpen((prev) => !prev)}
                  >
                    <svg
                      className="w-4 h-4 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="flex-1">Display</span>
                    <svg
                      className={`w-4 h-4 shrink-0 text-gray-500 transition-transform duration-200 ${displayOpen ? "rotate-90" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                  {displayOpen && (
                    <div className="pl-5 pr-2 pb-2 mt-0.5 border-l-2 border-border/50 ml-4">
                      {displayOptions.map(({ value, label, icon: Icon }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            handleThemeChange(value);
                            setDisplayOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-light-1/80 text-sm text-text text-left transition-colors rounded-lg"
                        >
                          <Icon className="w-4 h-4 shrink-0 text-gray-500" />
                          <span className="flex-1">{label}</span>
                          {theme === value && <CheckIcon className="w-4 h-4 shrink-0 text-pri" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {user?.role === "ADMIN" && (
                  <>
                    <Link to="/support" onClick={() => setProfileOpen(false)} className="block">
                      <DropdownItem
                        className="px-3 py-2.5 hover:bg-light-1/80 text-sm rounded-lg transition-colors text-text"
                        icon={
                          <svg
                            className="size-4 shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                            />
                          </svg>
                        }
                      >
                        Support
                      </DropdownItem>
                    </Link>
                  </>
                )}
              </nav>
              <div className="border-t border-border py-2 px-2">
                <DropdownItem
                  onClick={() => {
                    setProfileOpen(false);
                    handleLogout();
                  }}
                  icon={
                    <svg
                      className="w-4 h-4 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                  }
                  className="px-3 py-2.5 hover:bg-red-500/10 hover:text-red-400 cursor-pointer text-sm rounded-lg transition-colors"
                >
                  Log out
                </DropdownItem>
              </div>
            </Dropdown>
          </div>
        </div>
      </header>

      {isWidgetPage ? (
        <div className="pb-2">
          <div className="h-[calc(100vh-var(--navbar-height)-0.5rem)] rounded-2xl bg-bg flex gap-2">
            <main className="flex-1 bg-bg rounded-xl sm-scroll">
              <Suspense
                fallback={
                  <Loader wrapperClassName="min-h-[calc(100vh-var(--navbar-height)-0.5rem)]! w-full" />
                }
              >
                <Outlet />
              </Suspense>
            </main>
          </div>
        </div>
      ) : (
        <div className="pb-2">
          <div className="h-[calc(100vh-var(--navbar-height)-0.5rem)] rounded-2xl bg-bg p-2 flex gap-2">
            {/* Sidebar */}
            <Sidebar sidebarOpen={sidebarOpen} isMobile={isMobile} user={user} />

            {/* Page Content */}
            <main className="flex-1 bg-layout rounded-xl px-4 pb-4 pt-6 sm-scroll">
              {/* TODO: check this component*/}
              {/* <Breadcrumbs /> */}
              <PaymentSetupBanner />
              <Suspense
                fallback={
                  <Loader wrapperClassName="min-h-[calc(100vh-var(--navbar-height)-0.5rem)]! w-full" />
                }
              >
                <Outlet />
              </Suspense>
            </main>

            {/* TODO: Redesign it*/}
            {/* Mobile Overlay */}
            {sidebarOpen && isMobile && (
              <div
                className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                onClick={() => setSidebarOpen(false)}
              />
            )}
          </div>
        </div>
      )}

      {/* TODO: Redesign it*/}
      <ActiveCallDock />
    </div>
  );
}
